import { AlertCircle, Inbox } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import type { DashboardDetailResponse } from "@/features/dashboard/types";
import { useDelayedFlag } from "@/hooks/useDelayedFlag";
import { dashboardScrollbarStyle } from "./dashboardStyles";
import {
  DashboardDetailItems,
  type DashboardDetailItem,
  type DashboardDetailPresentation,
} from "./DashboardDetailItems";

export interface DashboardDetailContentProps {
  presentation: DashboardDetailPresentation;
  pages?: readonly DashboardDetailResponse[];
  isPending: boolean;
  isFetchingNextPage: boolean;
  error: unknown;
  hasNextPage: boolean;
  onLoadMore: () => void;
  onRetry?: () => void;
  loadingDelayMs?: number;
}

export function DashboardDetailContent(props: DashboardDetailContentProps) {
  const {
    pages,
    presentation,
    isPending,
    isFetchingNextPage,
    error,
    hasNextPage,
    onLoadMore,
    onRetry,
    loadingDelayMs = 200,
  } = props;
  const showLoading = useDelayedFlag(
    isPending && !pages?.length,
    loadingDelayMs,
  );
  const entity = pages?.[0]?.entity;
  const items =
    pages?.flatMap((page) => page.items as DashboardDetailItem[]) ?? [];

  if (isPending && !pages?.length && !error) {
    return (
      <div
        data-testid="dashboard-detail-loading"
        role="status"
        aria-busy="true"
        aria-live="polite"
        style={{
          minHeight: "8rem",
          display: "grid",
          placeItems: "center",
          color: "var(--dashboard-body-secondary)",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "0.9rem",
        }}
      >
        {showLoading ? "Carregando detalhes…" : null}
      </div>
    );
  }

  if (error && !pages?.length) {
    return (
      <DetailState
        role="alert"
        icon={<AlertCircle aria-hidden="true" size={20} />}
        title="Não foi possível carregar os detalhes."
        description="Tente novamente em instantes."
        actionLabel={onRetry ? "Tentar novamente" : undefined}
        onAction={onRetry}
      />
    );
  }

  return (
    <div
      data-testid="dashboard-detail-scroll-region"
      className="dashboard-subtle-scrollbar"
      style={{
        display: "flex",
        flex: "1 1 auto",
        flexDirection: "column",
        gap: "0.875rem",
        minHeight: 0,
        minWidth: 0,
        overflowY: "auto",
        overflowX: "hidden",
        overscrollBehavior: "contain",
        paddingRight: "0.2rem",
      }}
    >
      <style>{`${dashboardScrollbarStyle}
        .dashboard-detail-action {
          transition: background 0.14s ease, color 0.14s ease, opacity 0.14s ease, transform 0.14s ease;
        }
        .dashboard-detail-action:not(:disabled):hover {
          background: color-mix(in srgb, var(--login-accent) 13%, var(--login-bg));
        }
        .dashboard-detail-action:not(:disabled):active { transform: translateY(1px); }
        .dashboard-detail-action:focus-visible {
          outline: 2px solid var(--login-accent);
          outline-offset: 2px;
        }
        @media (prefers-reduced-motion: reduce) {
          .dashboard-detail-action {
            transition-duration: 0.01ms !important;
            transform: none !important;
          }
        }
      `}</style>

      {entity ? <EntityHeading name={entity.name} /> : null}

      {error && items.length > 0 ? (
        <div
          role="alert"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.75rem",
            border: "1px solid var(--dashboard-semantic-warning)",
            borderRadius: "0.5rem",
            color: "var(--dashboard-body-secondary)",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.84rem",
          }}
        >
          <AlertCircle
            aria-hidden="true"
            size={18}
            color="var(--dashboard-semantic-warning)"
          />
          <span>Alguns detalhes não puderam ser carregados.</span>
        </div>
      ) : null}

      {items.length === 0 ? (
        <DetailState
          role="status"
          icon={<Inbox aria-hidden="true" size={20} />}
          title="Nenhum detalhe encontrado para este período."
          description="Não há registros no contexto selecionado."
        />
      ) : (
        <DashboardDetailItems
          items={items}
          entityKind={entity?.kind}
          presentation={presentation}
        />
      )}

      {hasNextPage ? (
        <button
          type="button"
          className="dashboard-detail-action"
          aria-label={isFetchingNextPage ? "Carregando mais…" : "Carregar mais"}
          disabled={isFetchingNextPage}
          onClick={onLoadMore}
          style={{
            minHeight: "44px",
            flex: "0 0 auto",
            border: "1px solid var(--login-border)",
            borderRadius: "0.5rem",
            background: "var(--login-bg)",
            color: "var(--login-text)",
            cursor: isFetchingNextPage ? "wait" : "pointer",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.86rem",
            fontWeight: 700,
            opacity: isFetchingNextPage ? 0.65 : 1,
          }}
        >
          {isFetchingNextPage ? "Carregando mais…" : "Carregar mais"}
        </button>
      ) : null}
    </div>
  );
}

const detailCardStyle: CSSProperties = {
  display: "grid",
  gap: "0.65rem",
  minWidth: 0,
  padding: "0.875rem",
  border: "1px solid var(--login-border)",
  borderRadius: "0.625rem",
  background: "var(--login-bg)",
  color: "var(--login-text)",
};

function EntityHeading({ name }: { name: string }) {
  return (
    <h3
      title={name}
      style={{
        margin: 0,
        minWidth: 0,
        color: "var(--login-text)",
        fontFamily: "'Barlow Condensed', sans-serif",
        fontSize: "clamp(1.1rem, 3vw, 1.4rem)",
        fontWeight: 700,
        lineHeight: 1.15,
        overflowWrap: "anywhere",
      }}
    >
      {name}
    </h3>
  );
}

interface DetailStateProps {
  role: "alert" | "status";
  icon: ReactNode;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}

function DetailState({
  role,
  icon,
  title,
  description,
  actionLabel,
  onAction,
}: DetailStateProps) {
  return (
    <div
      role={role}
      style={{
        ...detailCardStyle,
        gridTemplateColumns: "auto minmax(0, 1fr)",
        alignItems: "start",
        color: "var(--dashboard-body-secondary)",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <span
        aria-hidden="true"
        style={{ color: "var(--login-accent)", display: "inline-flex" }}
      >
        {icon}
      </span>
      <div style={{ minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            color: "var(--login-text)",
            fontSize: "0.92rem",
            fontWeight: 700,
          }}
        >
          {title}
        </p>
        <p style={{ margin: "0.25rem 0 0", fontSize: "0.82rem" }}>
          {description}
        </p>
        {actionLabel && onAction ? (
          <button
            type="button"
            className="dashboard-detail-action"
            onClick={onAction}
            style={{
              minHeight: "44px",
              marginTop: "0.65rem",
              padding: "0 0.875rem",
              border: "1px solid var(--login-border)",
              borderRadius: "0.5rem",
              background: "var(--login-surface)",
              color: "var(--login-text)",
              cursor: "pointer",
              font: "inherit",
              fontWeight: 700,
            }}
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}
